import React, { useState, useEffect, useRef, useCallback } from 'react';
import { View, StyleSheet, Platform, Image, TouchableOpacity, useWindowDimensions, ActivityIndicator, ScrollView } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Ionicons from '@expo/vector-icons/Ionicons';
import { layout } from '../styles';
import { api, getFileUrl } from '../services/api';
import { retryAsync } from '../utils/retry';
import { PublicMasterProfile } from '../types/auth';
import { RootStackParamList } from '../navigation/types';
import { Typography, Button, SectionHeader } from './ui';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useScreenSize } from '../hooks/useScreenSize';
import { useI18n } from '../i18n';
import { typographyTokens } from '../styles/typographyTokens';

import { ACCENT_TEXT } from '../styles/accentText';
interface MasterCardProps {
  id: number;
  // Owning auth user id from the public master profile DTO. When present we
  // navigate into the unified /u/:userId profile; otherwise we fall back to
  // the legacy /masters/:id route for masters returned by an older BE.
  userId?: string;
  image: string | any;
  name: string;
  intention?: string | null;
  masterBadge: string;
  fallbackDesc: string;
  wasDragging?: () => boolean;
}

const MasterCard: React.FC<MasterCardProps> = ({ id, userId, image, name, intention, masterBadge, fallbackDesc, wasDragging }) => {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { isDark, colors: themeColors } = useTheme();

  const displayName = name;
  const displayDesc = intention || fallbackDesc;

  const shortDesc = displayDesc.length > 140
    ? displayDesc.slice(0, 140).trim() + '...'
    : displayDesc;

  const imageSource = typeof image === 'string' ? { uri: image } : image;
  // Initials placeholder when no real master photo is available — same
  // approach as MastersScreen so masters without `photoUrl` look honest
  // (initials on a colour) rather than wearing a stranger's stock photo.
  const initialsPalette = ['#E98245', '#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EC4899', '#06B6D4', '#84CC16'];
  const placeholderColour = initialsPalette[id % initialsPalette.length];
  const initials = (() => {
    const parts = (displayName || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return (parts[0]?.slice(0, 2) || '·').toUpperCase();
  })();

  return (
    <View style={styles.masterCard}>
      <TouchableOpacity
        style={[styles.cardWrapper, {
          borderRadius: 20,
          overflow: 'hidden',
          ...(Platform.OS === 'web' && {
            boxShadow: isDark ? '0 8px 28px rgba(0,0,0,0.4)' : '0 8px 28px rgba(0,0,0,0.06)',
            transition: 'transform 0.3s cubic-bezier(0.16,1,0.3,1), box-shadow 0.3s ease',
          } as any),
        }]}
        onPress={() => {
          if (wasDragging?.()) return;
          if (userId) {
            navigation.navigate('PublicUserProfile', { userId });
          } else {
            navigation.navigate('MasterDetail', { masterId: id });
          }
        }}
        activeOpacity={0.92}
        className={Platform.OS === 'web' ? 'base-hover' : ''}
      >
        <View style={[styles.imageContainer, { borderRadius: 20, backgroundColor: isDark ? '#1A1A1A' : '#EFEFEF' }]}>
          {image ? (
            <Image source={imageSource} style={styles.masterImage} />
          ) : (
            <View style={[styles.imagePlaceholder, { backgroundColor: placeholderColour, alignItems: 'center', justifyContent: 'center' } as any]}>
              <Typography variant="h1" style={{ color: '#FFF' } as any}>
                {initials}
              </Typography>
            </View>
          )}

          {/* MASTER badge — top left, glass */}
          <View style={styles.timeTag} className={Platform.OS === 'web' ? 'convex-glass' : ''}>
            <Typography variant="captionAccent" style={styles.timeText}>
              {masterBadge.toUpperCase()}
            </Typography>
          </View>

          {/* Bottom gradient blur overlay with name + desc */}
          {Platform.OS === 'web' ? (
            <div style={{
              position: 'absolute', left: 0, right: 0, bottom: 0,
              padding: '20px 16px 16px',
              background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.4) 60%, transparent 100%)',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
            }}>
              <Typography variant="bodyAccent" style={{ color: '#FFFFFF', marginBottom: 4 } as any} numberOfLines={1}>
                {displayName}
              </Typography>
              <Typography variant="caption" style={{ color: 'rgba(255,255,255,0.7)' } as any} numberOfLines={2}>
                {shortDesc}
              </Typography>
            </div>
          ) : (
            <View style={{
              position: 'absolute', left: 0, right: 0, bottom: 0,
              padding: 16, paddingTop: 20,
              backgroundColor: 'rgba(0,0,0,0.5)',
            }}>
              <Typography variant="bodyAccent" style={{ color: '#FFFFFF', marginBottom: 4 }} numberOfLines={1}>
                {displayName}
              </Typography>
              <Typography variant="caption" style={{ color: 'rgba(255,255,255,0.7)' }} numberOfLines={2}>
                {shortDesc}
              </Typography>
            </View>
          )}
        </View>
      </TouchableOpacity>
    </View>
  );
};

export const Masters: React.FC = () => {
  const { isDark, colors: themeColors } = useTheme();
  const { user } = useAuth();
  const { t } = useI18n('home');
  const { width } = useWindowDimensions();
  const { isTablet, isDesktop } = useScreenSize();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

  const [masters, setMasters] = useState<PublicMasterProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const dragRef = useRef<HTMLDivElement>(null);
  const dragState = useRef({ isDown: false, startX: 0, scrollLeft: 0, dragged: false });

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    const el = dragRef.current;
    if (!el) return;
    dragState.current = { isDown: true, startX: e.pageX, scrollLeft: el.scrollLeft, dragged: false };
  }, []);
  const onMouseUp = useCallback(() => { dragState.current.isDown = false; }, []);
  const onMouseMove = useCallback((e: React.MouseEvent) => {
    const s = dragState.current;
    if (!s.isDown) return;
    const dx = Math.abs(e.pageX - s.startX);
    if (dx > 5) s.dragged = true;
    e.preventDefault();
    const el = dragRef.current!;
    el.scrollLeft = s.scrollLeft - (e.pageX - s.startX);
  }, []);
  const wasDragging = useCallback(() => dragState.current.dragged, []);

  useEffect(() => {
    let cancelled = false;
    const loadMasters = async () => {
      try {
        // CORE-276 — retry transient failures (RU edge 504s under burst load;
        // the api-ru fallback doesn't retry 5xx) so the masters row recovers
        // instead of staying empty after a hard refresh.
        // CORE-322 — ranked home carousel (only masters with material).
        const data = await retryAsync(() => api.getPublicMasterProfiles({ sort: 'ranked' }), { retries: 4, baseDelayMs: 500 });
        if (!cancelled) setMasters(data);
      } catch (err) {
        console.error('Failed to load masters:', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    loadMasters();
    return () => { cancelled = true; };
  }, []);

  // Real photo only — no stock fallback. When `originalUrl` is null we return
  // null and let the card render an initials placeholder instead. Showing
  // unrelated stock photos under a master's name was creating profile
  // mismatches (e.g. mountain shot on Masters list vs lotus avatar on
  // /u/:userId for the same person).
  const getMasterImage = (_index: number, originalUrl: string | null) => {
    return originalUrl ? { uri: originalUrl } : null;
  };

  const getDisplayMaster = (master: PublicMasterProfile): PublicMasterProfile => {
    const isOwnMaster =
      (!!user?.id && master.userId === user.id) ||
      (user?.masterProfileId != null && master.id === user.masterProfileId);
    if (!isOwnMaster || !user) return master;
    return {
      ...master,
      name: user.resolvedDisplayName || user.displayName || master.name,
      photoUrl: user.avatarUrl || master.photoUrl,
    };
  };

  const isMobile = !isTablet && !isDesktop;

  return (
    <View style={[styles.section, isMobile && { paddingBottom: 100, borderBottomWidth: isDark ? 1 : 0, borderBottomColor: 'rgba(255,255,255,0.06)' }, { backgroundColor: themeColors.pageBg }]}>
      <View style={styles.container}>
        <SectionHeader
          title={t('masters.title')}
          titleHighlight={t('masters.titleHighlight')}

          actionText={t('masters.exploreAll')}
          onActionPress={() => navigation.navigate('Masters')}
        />

        {isLoading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="small" color={ACCENT_TEXT} />
          </View>
        ) : (
          Platform.OS === 'web' ? (
            <div
              ref={dragRef as any}
              className="drag-scroll"
              onMouseDown={onMouseDown as any}
              onMouseUp={onMouseUp as any}
              onMouseLeave={onMouseUp as any}
              onMouseMove={onMouseMove as any}
              style={{ display: 'flex', gap: 16, overflowX: 'auto', paddingBottom: 4 }}
            >
              {masters.map((master: PublicMasterProfile, index: number) => {
                const displayMaster = getDisplayMaster(master);
                return (
                  <MasterCard
                    key={master.id}
                    id={displayMaster.id}
                    userId={displayMaster.userId}
                    image={getMasterImage(index, getFileUrl(displayMaster.photoUrl))}
                    name={displayMaster.name}
                    intention={displayMaster.intention}
                    masterBadge={t('masters.masterBadge')}
                    fallbackDesc={t('masters.fallbackDesc')}
                    wasDragging={wasDragging}
                  />
                );
              })}
            </div>
          ) : (
            <ScrollView horizontal showsHorizontalScrollIndicator contentContainerStyle={{ gap: 16, paddingRight: 24 }}>
              {masters.map((master: PublicMasterProfile, index: number) => {
                const displayMaster = getDisplayMaster(master);
                return (
                  <MasterCard
                    key={master.id}
                    id={displayMaster.id}
                    userId={displayMaster.userId}
                    image={getMasterImage(index, getFileUrl(displayMaster.photoUrl))}
                    name={displayMaster.name}
                    intention={displayMaster.intention}
                    masterBadge={t('masters.masterBadge')}
                    fallbackDesc={t('masters.fallbackDesc')}
                    wasDragging={wasDragging}
                  />
                );
              })}
            </ScrollView>
          )
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  section: {
    paddingTop: Platform.OS === 'web' ? 40 : 32,
    paddingBottom: Platform.OS === 'web' ? 40 : 32,
    backgroundColor: 'transparent',
  },
  container: {
    maxWidth: layout.maxWidth,
    marginHorizontal: 'auto',
    paddingHorizontal: layout.paddingH,
    width: '100%',
  },
  header: {
    marginBottom: 80,
  },
  headerSubtitle: {
    marginBottom: 12,
  },
  headerTitle: {
    marginBottom: 24,
    maxWidth: 800,
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  headerDesc: {
    maxWidth: 620,
  },
  highlightWrapper: {
    borderBottomWidth: 4,
    borderBottomColor: 'rgba(237, 116, 61, 0.1)',
    marginHorizontal: 8,
  },
  highlightText: {
    color: ACCENT_TEXT,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginHorizontal: -12,
  },
  gridMedium: {},
  gridLarge: {},
  masterCard: {
    width: 360,
    flexShrink: 0,
  } as any,
  cardWrapper: {
    borderRadius: 32,
    overflow: 'hidden',
    ...(Platform.OS === 'web' && {
      boxShadow: '0 20px 50px rgba(0,0,0,0.08)',
      transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
    }),
  } as any,
  imageContainer: {
    aspectRatio: 0.85,
    width: '100%',
    position: 'relative',
    overflow: 'hidden',
  },
  masterImage: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  imagePlaceholder: {
    width: '100%',
    height: '100%',
    backgroundColor: '#F0EEEB',
    alignItems: 'center',
    justifyContent: 'center',
  },
  imageOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.03)',
  } as any,
  timeTag: {
    position: 'absolute',
    top: 16,
    left: 16,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 100,
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.3)',
    ...(Platform.OS === 'web' && {
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
    }),
  } as any,
  timeText: {
    ...typographyTokens.captionAccent,
    color: '#FFFFFF',
  } as any,
  content: {
    paddingHorizontal: 8,
    paddingVertical: 18,
    paddingBottom: 16,
  },
  masterName: {
    ...typographyTokens.uiH3,
    marginBottom: 6,
  },
  masterSpecialty: {
    ...typographyTokens.body,
    opacity: 0.8,
  },

  footerActions: {
    marginTop: 80,
    alignItems: 'flex-start',
  },
  viewAllButton: {
    minWidth: 280,
    borderRadius: 8,
  },
  loadingContainer: {
    padding: 100,
    alignItems: 'center',
  },
});
